import { AxiosError } from "axios";
import { useContacts } from "hooks/useContacts";
import { useCount } from "hooks/utils/useCount";
import { useMutation } from "react-query";
import { api } from "services/api";
import { Contact } from "types/Contact";
import { axiosError, axiosSuccess, handleRequestError, handleRequestSuccess } from "utils/Request";
import { useState } from "react";

type AssumeContactProps = {
  contact: Contact;
  force?: boolean;
};

async function assumeContact(contact: Contact, force: boolean = false): Promise<Contact> {
  const { data } = await api.post(`/contacts/${contact.id}/assume`, { force }).then(axiosSuccess).catch(axiosError);
  return data;
}

export function useAssumeContact() {
  const { selectContact } = useContacts();
  const { revalidateCount } = useCount();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentContact, setCurrentContact] = useState<Contact | null>(null);

  const mutation = useMutation(
    ({ contact, force }: AssumeContactProps) => assumeContact(contact, force),
    {
      onSuccess: (data: Contact) => {
        handleRequestSuccess("Você assumiu o contato com sucesso!");
        selectContact(data);
        revalidateCount();
      },
      onError: (error: AxiosError, variables: AssumeContactProps) => {
        if (error.response?.status === 421) {
          setCurrentContact(variables.contact);
          setIsModalOpen(true);
        } else {
          handleRequestError(error, 'Um erro ocorreu ao assumir o contato. Por favor, tente novamente!');
        }
      },
    }
  );

  const handleConfirm = () => {
    if (currentContact) {
      mutation.mutate({ contact: currentContact, force: true });
    }
    setIsModalOpen(false);
    setCurrentContact(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentContact(null);
  };

  return {
    ...mutation,
    isModalOpen,
    handleConfirm,
    handleCloseModal,
  };
}